import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, refreshSession } from "@/lib/api";
import { Page } from "@/components/Page";

const PRESETS = ["Was ziehe ich als Hochzeitsgast im Sommer an?", "Welche Farben passen zu meinem Hautton?", "Stell mir ein Office-Outfit aus meinem Schrank zusammen", "Welche Sneaker passen zu einer dunklen Jeans?"];

async function streamChat(message, onDelta) {
  const send = () => fetch(`${API}/chat/stream`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message }) });
  let res = await send();
  if (res.status === 401) { await refreshSession(); res = await send(); }
  if (!res.ok) throw new Error("Fehler");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const d = p.replace(/^data: /, "");
      if (d === "[DONE]") return;
      const j = JSON.parse(d);
      if (j.error) throw new Error(j.error);
      if (j.delta) onDelta(j.delta);
    }
  }
}

export default function Stylist() {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);

  useEffect(() => { api.get("/chat/history").then(({ data }) => setMsgs(data)); }, []);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [msgs]);

  const ask = async (q) => {
    const m = (q ?? text).trim();
    if (!m || busy) return;
    setText(""); setBusy(true);
    setMsgs((s) => [...s, { role: "user", content: m }, { role: "assistant", content: "" }]);
    try {
      await streamChat(m, (d) => setMsgs((s) => { const c = [...s]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + d }; return c; }));
    } catch (e) {
      toast.error(e.message || "Der Stylist ist gerade nicht erreichbar");
      setMsgs((s) => s.slice(0, -1));
    }
    setBusy(false);
  };

  const clear = async () => { await api.delete("/chat/history"); setMsgs([]); };

  return (
    <Page over="KI-Stylist" title="Frag deinen Stylist" sub="Dein persönlicher Mode-Berater kennt deinen Kleiderschrank, deinen Avatar und alle Partner-Marken."
      right={msgs.length > 0 && <button onClick={clear} className="btn-ghost !py-2" data-testid="chat-clear-button"><Trash2 className="h-4 w-4" /> Verlauf löschen</button>} testid="stylist-page">
      <div className="surface flex h-[62vh] flex-col overflow-hidden">
        <div className="flex-1 space-y-5 overflow-y-auto p-6" data-testid="chat-messages">
          {msgs.length === 0 && (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <img src="/logo.png" alt="vyne" className="h-16 w-16 rounded-[22%]" />
              <p className="mt-5 font-serif text-2xl">Wie kann ich dich heute stylen?</p>
              <div className="mt-6 flex max-w-2xl flex-wrap justify-center gap-2">
                {PRESETS.map((p, i) => <button key={p} onClick={() => ask(p)} className="rounded-full border border-white/10 px-4 py-2 text-xs text-neutral-300 transition-colors hover:border-[#D4AF37]/50" data-testid={`chat-preset-${i}`}>{p}</button>)}
              </div>
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.role}-${i}`}>
              <div className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-5 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-[#FAF7F0] text-black" : "border border-white/[0.08] bg-[#18181C] text-neutral-200"}`}>
                {m.role === "assistant" && <Sparkles className="mb-2 h-3.5 w-3.5 text-[#D4AF37]" />}
                {m.content.replace(/\*\*/g, "") || <span className="animate-pulse text-neutral-500">Stylist schreibt…</span>}
              </div>
            </div>
          ))}
          <div ref={end} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-3 border-t border-white/[0.08] p-4">
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Frag z.B.: Was passt zu meinem beigen Trenchcoat?" className="h-12 flex-1 rounded-full border border-white/10 bg-[#0A0A0B] px-5 text-sm outline-none focus:border-[#D4AF37]/60" data-testid="chat-input" />
          <button type="submit" disabled={busy || !text.trim()} className="btn-cream !px-5" data-testid="chat-send-button"><Send className="h-4 w-4" /></button>
        </form>
      </div>
    </Page>
  );
}
